// Premium SVG thumbnails for blog cards.
//
// Each design is defined ONCE as an <svg><symbol> in `BlogThumbnailSprite`
// (mount it once per page, e.g. above the post grid) and every card instance
// just references it via <use>. Rendering the full shape tree per-card was
// both wasteful (dozens of DOM nodes × ~50 posts) and actually broken: the
// gradient/filter/clipPath ids below (e.g. "nd-glow") are static, so multiple
// cards of the same category produced duplicate ids — invalid HTML that only
// "looked right" because browsers resolve url(#id) to the first match.
// Symbols scope those ids to a single definition, fixing both issues.

interface Props { category: string }

const CATEGORY_TO_SYMBOL: Record<string, string> = {
  Rollups: "thumb-rollups",
  Appchains: "thumb-appchains",
  Validators: "thumb-validators",
  Infrastructure: "thumb-infrastructure",
  Nodes: "thumb-nodes",
  Research: "thumb-research",
  Partnerships: "thumb-partnerships",
};

export default function BlogThumbnail({ category }: Props) {
  const id = CATEGORY_TO_SYMBOL[category] ?? "thumb-nodes";
  return (
    <svg viewBox="0 0 320 160" className="absolute inset-0 h-full w-full" aria-hidden focusable="false">
      <use href={`#${id}`} />
    </svg>
  );
}

// Mount once per page (before any <BlogThumbnail> usage) — defines every
// design's shapes/gradients/filters exactly once regardless of post count.
export function BlogThumbnailSprite() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden focusable="false">
      {/* ── NODES ───────────────────────────────────────────────────────────────
          Glowing network graph: hub node with radial aura, 9 peer nodes,
          spoke + ring edges, traveling data-packet diamonds */}
      <symbol id="thumb-nodes" viewBox="0 0 320 160" fill="none">
        <defs>
          <radialGradient id="nd-aura" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#9B8EFF" stopOpacity="0.50" />
            <stop offset="55%" stopColor="#9B8EFF" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#9B8EFF" stopOpacity="0" />
          </radialGradient>
          <filter id="nd-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="nd-softglow" x="-120%" y="-120%" width="340%" height="340%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
        </defs>
        {/* Hub radial aura */}
        <circle cx={160} cy={80} r="60" fill="url(#nd-aura)" />
        {/* Outer ring edges */}
        <line x1={42} y1={44} x2={112} y2={20} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        <line x1={112} y1={20} x2={194} y2={35} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        <line x1={194} y1={35} x2={262} y2={26} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        <line x1={262} y1={26} x2={283} y2={80} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        <line x1={283} y1={80} x2={258} y2={134} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        <line x1={258} y1={134} x2={164} y2={148} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        <line x1={164} y1={148} x2={76} y2={130} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        <line x1={76} y1={130} x2={24} y2={88} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        <line x1={24} y1={88} x2={42} y2={44} stroke="#9B8EFF" strokeWidth="0.75" strokeOpacity="0.18" />
        {/* Hub spoke edges */}
        <line x1={160} y1={80} x2={42} y2={44} stroke="#9B8EFF" strokeWidth="0.9" strokeOpacity="0.30" />
        <line x1={160} y1={80} x2={112} y2={20} stroke="#9B8EFF" strokeWidth="0.9" strokeOpacity="0.30" />
        <line x1={160} y1={80} x2={194} y2={35} stroke="#9B8EFF" strokeWidth="0.9" strokeOpacity="0.30" />
        <line x1={160} y1={80} x2={283} y2={80} stroke="#9B8EFF" strokeWidth="0.9" strokeOpacity="0.30" />
        <line x1={160} y1={80} x2={258} y2={134} stroke="#9B8EFF" strokeWidth="0.9" strokeOpacity="0.30" />
        <line x1={160} y1={80} x2={76} y2={130} stroke="#9B8EFF" strokeWidth="0.9" strokeOpacity="0.30" />
        {/* Traveling data packets (rotated squares on spokes) */}
        <rect x={174} y={54.5} width="6" height="6" fill="#9B8EFF" fillOpacity="0.90" transform="rotate(45,177,57.5)" />
        <rect x={206} y={104} width="6" height="6" fill="#9B8EFF" fillOpacity="0.90" transform="rotate(45,209,107)" />
        <rect x={269.5} y={50} width="6" height="6" fill="#9B8EFF" fillOpacity="0.90" transform="rotate(45,272.5,53)" />
        {/* Peer nodes: outer halo + filled core */}
        <g><circle cx={42} cy={44} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={42} cy={44} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        <g><circle cx={112} cy={20} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={112} cy={20} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        <g><circle cx={194} cy={35} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={194} cy={35} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        <g><circle cx={262} cy={26} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={262} cy={26} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        <g><circle cx={283} cy={80} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={283} cy={80} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        <g><circle cx={258} cy={134} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={258} cy={134} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        <g><circle cx={164} cy={148} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={164} cy={148} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        <g><circle cx={76} cy={130} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={76} cy={130} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        <g><circle cx={24} cy={88} r="7" fill="#9B8EFF" fillOpacity="0.08" /><circle cx={24} cy={88} r="3" fill="#9B8EFF" fillOpacity="0.72" /></g>
        {/* Hub: soft bloom behind, concentric rings, glowing core, white hot center */}
        <circle cx={160} cy={80} r="10" fill="#9B8EFF" fillOpacity="0.55" filter="url(#nd-softglow)" />
        <circle cx={160} cy={80} r="26" stroke="#9B8EFF" strokeOpacity="0.13" strokeWidth="1" />
        <circle cx={160} cy={80} r="16" stroke="#9B8EFF" strokeOpacity="0.25" strokeWidth="1" />
        <circle cx={160} cy={80} r="8" fill="#9B8EFF" fillOpacity="0.95" filter="url(#nd-glow)" />
        <circle cx={160} cy={80} r="3" fill="white" fillOpacity="0.92" />
      </symbol>

      {/* ── RESEARCH ────────────────────────────────────────────────────────────
          Ascending analytics bars with a glowing trend line, inspected by a
          magnifying lens with a glass-highlight arc + scattered data points */}
      <symbol id="thumb-research" viewBox="0 0 320 160" fill="none">
        <defs>
          <radialGradient id="rs-aura" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2DD4BF" stopOpacity="0.42" />
            <stop offset="55%" stopColor="#2DD4BF" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#2DD4BF" stopOpacity="0" />
          </radialGradient>
          <filter id="rs-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <clipPath id="rs-lens-clip">
            <circle cx={207} cy={70} r="32" />
          </clipPath>
        </defs>
        {/* Ambient aura behind the lens */}
        <circle cx={207} cy={70} r="58" fill="url(#rs-aura)" />
        {/* Ascending bar chart */}
        <rect x={34}  y={112} width="16" height="26" rx="2" fill="#2DD4BF" fillOpacity="0.18" stroke="#2DD4BF" strokeOpacity="0.45" strokeWidth="1" />
        <rect x={64}  y={98}  width="16" height="40" rx="2" fill="#2DD4BF" fillOpacity="0.22" stroke="#2DD4BF" strokeOpacity="0.45" strokeWidth="1" />
        <rect x={94}  y={80}  width="16" height="58" rx="2" fill="#2DD4BF" fillOpacity="0.26" stroke="#2DD4BF" strokeOpacity="0.45" strokeWidth="1" />
        <rect x={124} y={60}  width="16" height="78" rx="2" fill="#2DD4BF" fillOpacity="0.30" stroke="#2DD4BF" strokeOpacity="0.45" strokeWidth="1" />
        <rect x={154} y={44}  width="16" height="94" rx="2" fill="#2DD4BF" fillOpacity="0.34" stroke="#2DD4BF" strokeOpacity="0.45" strokeWidth="1" />
        {/* Trend line across bar tops, glowing */}
        <polyline
          points="42,112 72,98 102,80 132,60 162,44"
          stroke="#2DD4BF"
          strokeOpacity="0.85"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#rs-glow)"
        />
        <circle cx={162} cy={44} r="3" fill="#2DD4BF" fillOpacity="0.95" filter="url(#rs-glow)" />
        {/* Magnified chart preview inside the lens glass */}
        <g clipPath="url(#rs-lens-clip)" opacity="0.55">
          <rect x={183} y={82} width="14" height="24" fill="#2DD4BF" fillOpacity="0.20" />
          <rect x={201} y={70} width="14" height="36" fill="#2DD4BF" fillOpacity="0.26" />
          <rect x={219} y={54} width="14" height="52" fill="#2DD4BF" fillOpacity="0.32" />
        </g>
        {/* Lens rim + glass highlight */}
        <circle cx={207} cy={70} r="32" fill="#2DD4BF" fillOpacity="0.05" stroke="#2DD4BF" strokeOpacity="0.65" strokeWidth="2" />
        <circle cx={207} cy={70} r="25" stroke="#2DD4BF" strokeOpacity="0.20" strokeWidth="1" />
        <path d="M191,54 A24,24 0 0,1 218,49" stroke="white" strokeOpacity="0.35" strokeWidth="2" strokeLinecap="round" fill="none" />
        {/* Handle */}
        <line x1={230} y1={93} x2={254} y2={118} stroke="#2DD4BF" strokeOpacity="0.75" strokeWidth="5" strokeLinecap="round" />
        {/* Scattered data points */}
        <circle cx={270} cy={40} r="1.6" fill="#2DD4BF" fillOpacity="0.35" />
        <circle cx={286} cy={64} r="1.6" fill="#2DD4BF" fillOpacity="0.45" />
        <circle cx={276} cy={92} r="1.6" fill="#2DD4BF" fillOpacity="0.30" />
        <circle cx={252} cy={30} r="1.6" fill="#2DD4BF" fillOpacity="0.40" />
        <circle cx={298} cy={110} r="1.6" fill="#2DD4BF" fillOpacity="0.25" />
      </symbol>

      {/* ── ROLLUPS ─────────────────────────────────────────────────────────────
          True isometric 3-layer stack, top slab with internal grid lines,
          upward data-flow arrow with connector dots */}
      <symbol id="thumb-rollups" viewBox="0 0 320 160" fill="none">
        <defs>
          <filter id="rl-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <clipPath id="rl-top-clip">
            <polygon points="76,74 240,74 220,54 56,54" />
          </clipPath>
        </defs>
        {/* Slab 1 (bottom) */}
        <g>
          <polygon points="264,118 244,98 244,114 264,134" fill="#7B6CF6" fillOpacity="0.25" stroke="#7B6CF6" strokeOpacity="0.40" strokeWidth="0.8" />
          <polygon points="56,118 264,118 264,134 56,134" fill="#7B6CF6" fillOpacity="0.20" stroke="#7B6CF6" strokeOpacity="0.40" strokeWidth="0.8" />
          <polygon points="56,118 264,118 244,98 36,98" fill="#7B6CF6" fillOpacity="0.10" stroke="#7B6CF6" strokeOpacity="0.35" strokeWidth="1.3" />
        </g>
        {/* Slab 2 (middle) */}
        <g>
          <polygon points="252,96 232,76 232,92 252,112" fill="#7B6CF6" fillOpacity="0.30" stroke="#7B6CF6" strokeOpacity="0.40" strokeWidth="0.8" />
          <polygon points="66,96 252,96 252,112 66,112" fill="#7B6CF6" fillOpacity="0.25" stroke="#7B6CF6" strokeOpacity="0.40" strokeWidth="0.8" />
          <polygon points="66,96 252,96 232,76 46,76" fill="#7B6CF6" fillOpacity="0.15" stroke="#7B6CF6" strokeOpacity="0.47" strokeWidth="1.3" />
        </g>
        {/* Slab 3 (top) */}
        <g>
          <polygon points="240,74 220,54 220,70 240,90" fill="#7B6CF6" fillOpacity="0.37" stroke="#7B6CF6" strokeOpacity="0.40" strokeWidth="0.8" />
          <polygon points="76,74 240,74 240,90 76,90" fill="#7B6CF6" fillOpacity="0.32" stroke="#7B6CF6" strokeOpacity="0.40" strokeWidth="0.8" />
          <polygon points="76,74 240,74 220,54 56,54" fill="#7B6CF6" fillOpacity="0.22" stroke="#7B6CF6" strokeOpacity="0.59" strokeWidth="1.3" />
        </g>
        {/* Internal grid lines on top slab (clipped) */}
        <g clipPath="url(#rl-top-clip)" opacity="0.4">
          <line x1={100} y1={74} x2={80} y2={54} stroke="#7B6CF6" strokeWidth="0.8" strokeOpacity="0.7" />
          <line x1={130} y1={74} x2={110} y2={54} stroke="#7B6CF6" strokeWidth="0.8" strokeOpacity="0.7" />
          <line x1={160} y1={74} x2={140} y2={54} stroke="#7B6CF6" strokeWidth="0.8" strokeOpacity="0.7" />
          <line x1={190} y1={74} x2={170} y2={54} stroke="#7B6CF6" strokeWidth="0.8" strokeOpacity="0.7" />
          <line x1={220} y1={74} x2={200} y2={54} stroke="#7B6CF6" strokeWidth="0.8" strokeOpacity="0.7" />
        </g>
        {/* Connector port dots on front face of each slab */}
        <circle cx={160} cy={118} r="2.5" fill="#7B6CF6" fillOpacity="0.65" />
        <circle cx={159} cy={96} r="2.5" fill="#7B6CF6" fillOpacity="0.65" />
        <circle cx={158} cy={74} r="2.5" fill="#7B6CF6" fillOpacity="0.65" />
        {/* Upward data-flow arrow */}
        <line x1={160} y1={50} x2={160} y2={28} stroke="#7B6CF6" strokeOpacity="0.75" strokeWidth="2" strokeLinecap="round" filter="url(#rl-glow)" />
        <polyline points="151,36 160,28 169,36" stroke="#7B6CF6" strokeOpacity="0.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#rl-glow)" />
      </symbol>

      {/* ── VALIDATORS ──────────────────────────────────────────────────────────
          Security shield + inner hex pattern (clipped) + glowing checkmark +
          downward radar semi-arcs + corner status indicators */}
      <symbol id="thumb-validators" viewBox="0 0 320 160" fill="none">
        <defs>
          <radialGradient id="vl-fill" cx="50%" cy="35%" r="55%">
            <stop offset="0%" stopColor="#22C55E" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#22C55E" stopOpacity="0.04" />
          </radialGradient>
          <filter id="vl-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <clipPath id="vl-clip">
            <path d="M122,36 Q160,32 198,36 L198,92 Q160,118 122,92 Z" />
          </clipPath>
        </defs>
        {/* Radar semi-arcs (below shield apex, sweeping downward) */}
        <path d="M118,128 A42,42 0 0,0 202,128" stroke="#22C55E" strokeOpacity="0.07" strokeWidth="1" />
        <path d="M102,128 A58,58 0 0,0 218,128" stroke="#22C55E" strokeOpacity="0.065" strokeWidth="1" />
        <path d="M84,128 A76,76 0 0,0 236,128" stroke="#22C55E" strokeOpacity="0.06" strokeWidth="1" />
        <path d="M64,128 A96,96 0 0,0 256,128" stroke="#22C55E" strokeOpacity="0.055" strokeWidth="1" />
        {/* Shield body */}
        <path d="M114,30 Q160,24 206,30 L206,98 Q160,128 114,98 Z" fill="url(#vl-fill)" stroke="#22C55E" strokeOpacity="0.55" strokeWidth="1.6" />
        {/* Inner border */}
        <path d="M122,36 Q160,32 198,36 L198,92 Q160,118 122,92 Z" fill="none" stroke="#22C55E" strokeOpacity="0.18" strokeWidth="0.8" />
        {/* Hex-dot pattern inside shield (clipped) */}
        <g clipPath="url(#vl-clip)">
          <circle cx={140} cy={62} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
          <circle cx={160} cy={62} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
          <circle cx={180} cy={62} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
          <circle cx={150} cy={78} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
          <circle cx={170} cy={78} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
          <circle cx={140} cy={94} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
          <circle cx={160} cy={94} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
          <circle cx={180} cy={94} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
          <circle cx={160} cy={110} r="5" stroke="#22C55E" strokeWidth="0.8" strokeOpacity="0.28" fill="none" />
        </g>
        {/* Glowing checkmark */}
        <polyline points="141,84 156,100 184,68" stroke="#22C55E" strokeWidth="3.2" strokeOpacity="0.95" strokeLinecap="round" strokeLinejoin="round" filter="url(#vl-glow)" />
        {/* Corner status dots */}
        <circle cx={114} cy={30} r="3" fill="#22C55E" fillOpacity="0.65" />
        <circle cx={206} cy={30} r="3" fill="#22C55E" fillOpacity="0.65" />
        <circle cx={114} cy={98} r="2" fill="#22C55E" fillOpacity="0.45" />
        <circle cx={206} cy={98} r="2" fill="#22C55E" fillOpacity="0.45" />
      </symbol>

      {/* ── APPCHAINS ───────────────────────────────────────────────────────────
          4 protocol modules with header bands (clipped), alternating bezier
          arc connections, data-flow dots at arc midpoints, UI detail lines */}
      <symbol id="thumb-appchains" viewBox="0 0 320 160" fill="none">
        <defs>
          <clipPath id="ap-clip-0"><rect x={28} y={54} width={50} height={52} rx="6" /></clipPath>
          <clipPath id="ap-clip-1"><rect x={102} y={54} width={50} height={52} rx="6" /></clipPath>
          <clipPath id="ap-clip-2"><rect x={176} y={54} width={50} height={52} rx="6" /></clipPath>
          <clipPath id="ap-clip-3"><rect x={250} y={54} width={50} height={52} rx="6" /></clipPath>
        </defs>
        {/* Alternating bezier arc connections (up, down, up) */}
        <path d="M78,80 C90,56 90,56 102,80" stroke="#F59E0B" strokeOpacity="0.55" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M152,80 C164,104 164,104 176,80" stroke="#F59E0B" strokeOpacity="0.55" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M226,80 C238,56 238,56 250,80" stroke="#F59E0B" strokeOpacity="0.55" strokeWidth="1.4" strokeLinecap="round" />
        {/* Data-flow dot at arc midpoints */}
        <circle cx={90} cy={62} r="3" fill="#F59E0B" fillOpacity="0.70" />
        <circle cx={164} cy={98} r="3" fill="#F59E0B" fillOpacity="0.70" />
        <circle cx={238} cy={62} r="3" fill="#F59E0B" fillOpacity="0.70" />
        {/* Module blocks */}
        <g>
          <rect x={28} y={54} width={50} height={52} rx="6" fill="#F59E0B" fillOpacity="0.07" stroke="#F59E0B" strokeOpacity="0.55" strokeWidth="1.3" />
          <rect x={28} y={54} width={50} height={15} fill="#F59E0B" fillOpacity="0.20" clipPath="url(#ap-clip-0)" />
          <line x1={28} y1={69} x2={78} y2={69} stroke="#F59E0B" strokeOpacity="0.28" strokeWidth="0.8" />
          <circle cx={69} cy={61.5} r="2.8" fill="#F59E0B" fillOpacity="0.80" />
          <line x1={36} y1={80} x2={70} y2={80} stroke="#F59E0B" strokeOpacity="0.28" strokeWidth="1" />
          <line x1={36} y1={89} x2={64} y2={89} stroke="#F59E0B" strokeOpacity="0.20" strokeWidth="1" />
          <line x1={36} y1={98} x2={68} y2={98} stroke="#F59E0B" strokeOpacity="0.15" strokeWidth="1" />
        </g>
        <g>
          <rect x={102} y={54} width={50} height={52} rx="6" fill="#F59E0B" fillOpacity="0.09" stroke="#F59E0B" strokeOpacity="0.55" strokeWidth="1.3" />
          <rect x={102} y={54} width={50} height={15} fill="#F59E0B" fillOpacity="0.20" clipPath="url(#ap-clip-1)" />
          <line x1={102} y1={69} x2={152} y2={69} stroke="#F59E0B" strokeOpacity="0.28" strokeWidth="0.8" />
          <circle cx={143} cy={61.5} r="2.8" fill="#F59E0B" fillOpacity="0.80" />
          <line x1={110} y1={80} x2={144} y2={80} stroke="#F59E0B" strokeOpacity="0.28" strokeWidth="1" />
          <line x1={110} y1={89} x2={138} y2={89} stroke="#F59E0B" strokeOpacity="0.20" strokeWidth="1" />
          <line x1={110} y1={98} x2={142} y2={98} stroke="#F59E0B" strokeOpacity="0.15" strokeWidth="1" />
        </g>
        <g>
          <rect x={176} y={54} width={50} height={52} rx="6" fill="#F59E0B" fillOpacity="0.11" stroke="#F59E0B" strokeOpacity="0.55" strokeWidth="1.3" />
          <rect x={176} y={54} width={50} height={15} fill="#F59E0B" fillOpacity="0.20" clipPath="url(#ap-clip-2)" />
          <line x1={176} y1={69} x2={226} y2={69} stroke="#F59E0B" strokeOpacity="0.28" strokeWidth="0.8" />
          <circle cx={217} cy={61.5} r="2.8" fill="#F59E0B" fillOpacity="0.80" />
          <line x1={184} y1={80} x2={218} y2={80} stroke="#F59E0B" strokeOpacity="0.28" strokeWidth="1" />
          <line x1={184} y1={89} x2={212} y2={89} stroke="#F59E0B" strokeOpacity="0.20" strokeWidth="1" />
          <line x1={184} y1={98} x2={216} y2={98} stroke="#F59E0B" strokeOpacity="0.15" strokeWidth="1" />
        </g>
        <g>
          <rect x={250} y={54} width={50} height={52} rx="6" fill="#F59E0B" fillOpacity="0.13" stroke="#F59E0B" strokeOpacity="0.55" strokeWidth="1.3" />
          <rect x={250} y={54} width={50} height={15} fill="#F59E0B" fillOpacity="0.20" clipPath="url(#ap-clip-3)" />
          <line x1={250} y1={69} x2={300} y2={69} stroke="#F59E0B" strokeOpacity="0.28" strokeWidth="0.8" />
          <circle cx={291} cy={61.5} r="2.8" fill="#F59E0B" fillOpacity="0.80" />
          <line x1={258} y1={80} x2={292} y2={80} stroke="#F59E0B" strokeOpacity="0.28" strokeWidth="1" />
          <line x1={258} y1={89} x2={286} y2={89} stroke="#F59E0B" strokeOpacity="0.20" strokeWidth="1" />
          <line x1={258} y1={98} x2={290} y2={98} stroke="#F59E0B" strokeOpacity="0.15" strokeWidth="1" />
        </g>
      </symbol>

      {/* ── INFRASTRUCTURE ──────────────────────────────────────────────────────
          Server rack: 3 rack units with activity bars + glowing LED + status
          dots. Top-right uptime sparkline. Bottom: circuit trace tree */}
      <symbol id="thumb-infrastructure" viewBox="0 0 320 160" fill="none">
        <defs>
          <filter id="if-led" x="-120%" y="-120%" width="340%" height="340%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* Server rack units */}
        <g>
          <rect x={62} y={26} width={178} height={22} rx="3" fill="#38BDF8" fillOpacity="0.06" stroke="#38BDF8" strokeOpacity="0.38" strokeWidth="1.2" />
          <line x1={78} y1={26} x2={78} y2={48} stroke="#38BDF8" strokeOpacity="0.14" strokeWidth="0.7" />
          <circle cx={70} cy={37} r="1.8" fill="#38BDF8" fillOpacity="0.30" />
          <rect x={84} y={33} width={138} height="8" rx="2" fill="#38BDF8" fillOpacity="0.16" />
          <rect x={84} y={33} width={138} height="8" rx="2" fill="none" stroke="#38BDF8" strokeOpacity="0.18" strokeWidth="0.6" />
          <circle cx={222} cy={37} r="4.5" fill="#38BDF8" fillOpacity="0.80" filter="url(#if-led)" />
          <circle cx={222} cy={37} r="2.5" fill="#38BDF8" />
          <circle cx={210} cy={37} r="2.2" fill="#38BDF8" fillOpacity="0.28" />
          <circle cx={200} cy={37} r="2.2" fill="#38BDF8" fillOpacity="0.14" />
        </g>
        <g>
          <rect x={62} y={54} width={178} height={22} rx="3" fill="#38BDF8" fillOpacity="0.085" stroke="#38BDF8" strokeOpacity="0.38" strokeWidth="1.2" />
          <line x1={78} y1={54} x2={78} y2={76} stroke="#38BDF8" strokeOpacity="0.14" strokeWidth="0.7" />
          <circle cx={70} cy={65} r="1.8" fill="#38BDF8" fillOpacity="0.30" />
          <rect x={84} y={61} width={106} height="8" rx="2" fill="#38BDF8" fillOpacity="0.16" />
          <rect x={84} y={61} width={106} height="8" rx="2" fill="none" stroke="#38BDF8" strokeOpacity="0.18" strokeWidth="0.6" />
          <circle cx={222} cy={65} r="4.5" fill="#38BDF8" fillOpacity="0.80" filter="url(#if-led)" />
          <circle cx={222} cy={65} r="2.5" fill="#38BDF8" />
          <circle cx={210} cy={65} r="2.2" fill="#38BDF8" fillOpacity="0.28" />
          <circle cx={200} cy={65} r="2.2" fill="#38BDF8" fillOpacity="0.14" />
        </g>
        <g>
          <rect x={62} y={82} width={178} height={22} rx="3" fill="#38BDF8" fillOpacity="0.11" stroke="#38BDF8" strokeOpacity="0.38" strokeWidth="1.2" />
          <line x1={78} y1={82} x2={78} y2={104} stroke="#38BDF8" strokeOpacity="0.14" strokeWidth="0.7" />
          <circle cx={70} cy={93} r="1.8" fill="#38BDF8" fillOpacity="0.30" />
          <rect x={84} y={89} width={158} height="8" rx="2" fill="#38BDF8" fillOpacity="0.16" />
          <rect x={84} y={89} width={158} height="8" rx="2" fill="none" stroke="#38BDF8" strokeOpacity="0.18" strokeWidth="0.6" />
          <circle cx={222} cy={93} r="4.5" fill="#38BDF8" fillOpacity="0.80" filter="url(#if-led)" />
          <circle cx={222} cy={93} r="2.5" fill="#38BDF8" />
          <circle cx={210} cy={93} r="2.2" fill="#38BDF8" fillOpacity="0.28" />
          <circle cx={200} cy={93} r="2.2" fill="#38BDF8" fillOpacity="0.14" />
        </g>
        {/* Sparkline (uptime graph) — top-right corner */}
        <polyline points="252,46 258,38 264,42 272,30 280,36 288,28 296,34 304,24 312,30" stroke="#38BDF8" strokeOpacity="0.60" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M252,46 L258,38 L264,42 L272,30 L280,36 L288,28 L296,34 L304,24 L312,30 L312,52 L252,52 Z" fill="#38BDF8" fillOpacity="0.08" />
        <circle cx={312} cy={30} r="2.5" fill="#38BDF8" fillOpacity="0.70" />
        {/* Circuit trace tree below servers */}
        <line x1={152} y1={104} x2={152} y2={122} stroke="#38BDF8" strokeOpacity="0.24" strokeWidth="1" />
        <line x1={92} y1={122} x2={218} y2={122} stroke="#38BDF8" strokeOpacity="0.24" strokeWidth="1" />
        <line x1={92} y1={122} x2={92} y2={144} stroke="#38BDF8" strokeOpacity="0.17" strokeWidth="1" />
        <line x1={152} y1={122} x2={152} y2={144} stroke="#38BDF8" strokeOpacity="0.17" strokeWidth="1" />
        <line x1={218} y1={122} x2={218} y2={144} stroke="#38BDF8" strokeOpacity="0.17" strokeWidth="1" />
        <line x1={92} y1={136} x2={68} y2={136} stroke="#38BDF8" strokeOpacity="0.12" strokeWidth="1" />
        <line x1={68} y1={136} x2={68} y2={150} stroke="#38BDF8" strokeOpacity="0.12" strokeWidth="1" />
        <circle cx={92} cy={122} r="2.5" fill="#38BDF8" fillOpacity="0.45" />
        <circle cx={152} cy={122} r="2.5" fill="#38BDF8" fillOpacity="0.45" />
        <circle cx={218} cy={122} r="2.5" fill="#38BDF8" fillOpacity="0.45" />
        <circle cx={92} cy={136} r="2" fill="#38BDF8" fillOpacity="0.32" />
        <circle cx={68} cy={150} r="2" fill="#38BDF8" fillOpacity="0.25" />
        <circle cx={152} cy={144} r="2" fill="#38BDF8" fillOpacity="0.25" />
        <circle cx={218} cy={144} r="2" fill="#38BDF8" fillOpacity="0.25" />
      </symbol>

      {/* ── PARTNERSHIPS ────────────────────────────────────────────────────────
          Two interlocking rings (collaboration) with a glowing link node at
          the overlap, flanked by satellite connection points on each ring */}
      <symbol id="thumb-partnerships" viewBox="0 0 320 160" fill="none">
        <defs>
          <radialGradient id="pt-aura" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.40" />
            <stop offset="55%" stopColor="#A78BFA" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#A78BFA" stopOpacity="0" />
          </radialGradient>
          <filter id="pt-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* Ambient aura behind the overlap */}
        <circle cx={160} cy={80} r="60" fill="url(#pt-aura)" />
        {/* Left + right interlocking rings */}
        <circle cx={128} cy={80} r="44" fill="#A78BFA" fillOpacity="0.05" stroke="#A78BFA" strokeOpacity="0.55" strokeWidth="2" />
        <circle cx={192} cy={80} r="44" fill="#A78BFA" fillOpacity="0.05" stroke="#A78BFA" strokeOpacity="0.55" strokeWidth="2" />
        {/* Spokes to satellite connection points */}
        <line x1={128} y1={80} x2={70} y2={48} stroke="#A78BFA" strokeWidth="0.9" strokeOpacity="0.30" />
        <line x1={128} y1={80} x2={70} y2={112} stroke="#A78BFA" strokeWidth="0.9" strokeOpacity="0.30" />
        <line x1={192} y1={80} x2={250} y2={48} stroke="#A78BFA" strokeWidth="0.9" strokeOpacity="0.30" />
        <line x1={192} y1={80} x2={250} y2={112} stroke="#A78BFA" strokeWidth="0.9" strokeOpacity="0.30" />
        {/* Satellite connection points */}
        <g><circle cx={70} cy={48} r="6" fill="#A78BFA" fillOpacity="0.08" /><circle cx={70} cy={48} r="2.6" fill="#A78BFA" fillOpacity="0.70" /></g>
        <g><circle cx={70} cy={112} r="6" fill="#A78BFA" fillOpacity="0.08" /><circle cx={70} cy={112} r="2.6" fill="#A78BFA" fillOpacity="0.70" /></g>
        <g><circle cx={250} cy={48} r="6" fill="#A78BFA" fillOpacity="0.08" /><circle cx={250} cy={48} r="2.6" fill="#A78BFA" fillOpacity="0.70" /></g>
        <g><circle cx={250} cy={112} r="6" fill="#A78BFA" fillOpacity="0.08" /><circle cx={250} cy={112} r="2.6" fill="#A78BFA" fillOpacity="0.70" /></g>
        {/* Glowing link node at the overlap */}
        <circle cx={160} cy={80} r="9" fill="#A78BFA" fillOpacity="0.55" filter="url(#pt-glow)" />
        <circle cx={160} cy={80} r="3.5" fill="white" fillOpacity="0.92" />
      </symbol>
    </svg>
  );
}
